import type { DialogProps } from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Card, IconButton, MuiDialog, Typography } from '@utilisourcepackagelibdev/utilisourcepackagelib';
import { Divider, List, Link, ListItem, Stack, Theme, useMediaQuery } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';

interface IProps extends DialogProps {
  setOpen: (open: boolean) => void;
  onSuccess?: () => void;
  onClose?: () => void;
  disabled?: boolean;
  ticketData?: any;
  workflowData?: any;
  name?: string;
}

const FailedToArchiveModal = (props: IProps) => {
  const { onSuccess, setOpen, onClose, disabled, ticketData, workflowData, name, ...dialogProps } = props;
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

  const ticketCount = ticketData ? ticketData?.length : 0;
  const workflowCount = workflowData ? workflowData?.length : 0;

  return (
    <MuiDialog
      {...dialogProps}


      sx={{
        '& .MuiDialog-paper': {
          maxWidth: '40vw',
          width: '90%',
        },
      }}>
      {ticketCount > 10 || workflowCount > 10 ? (
        <DialogContent>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ textAlign: 'center', border: '10px solid red', p: 2, mt: 5 }}>
            <WarningIcon />
            <Typography variant="h6">
              {`Unable to archive "${name}" at this time. Please remove all ${
                ticketCount
                  ? `${ticketCount} 
                ticket${ticketCount > 1 ? 's' : ''}`
                  : ''
              } ${ticketCount && workflowCount ? 'and' : ''} 
                ${workflowCount ? `${workflowCount} workflow rule${workflowCount > 1 ? 's' : ''}` : ''} 
                assigned to "${name}" before archiving.`}
            </Typography>
            <WarningIcon />
          </Stack>
        </DialogContent>
      ) : (
        <>
          <DialogTitle sx={{ m: 5, mt: 10, backgroundColor: 'red' }}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ textAlign: 'center', backgroundColor: 'white', p: 2 }}>
              <WarningIcon />
              <Typography variant="h6">
                Unable to archive "{name}" at this time. Please remove all ticket(s) and workflow rule(s) assigned to "
                {name}" before archiving.
              </Typography>
              <WarningIcon />
            </Stack>
          </DialogTitle>
          <Divider />
          <DialogContent>
            <Stack direction={isDesktop ? 'row' : 'column'} spacing={5}>
              {ticketData && ticketCount < 10 && (
                <Card sx={{ p: 2, display: 'flex', gap: 5, flexDirection: 'column', flex: 1 }}>
                  <Typography variant="h6" sx={{ m: 0, p: 0 }}>
                    Tickets assigned to {name}
                  </Typography>
                  {ticketData && (
                    <List sx={{ m: 0, p: 0 }}>
                      {ticketData?.map((ticket: any) => {
                        return (
                          <ListItem key={ticket.TId} sx={{ m: 0, p: 0 }}>
                            <Link href={`/ticket/${ticket.TId}?variant=edit&step=0`}>
                              Ticket ID: {ticket.TId} - {ticket.Title}
                            </Link>
                          </ListItem>
                        );
                      })}
                    </List>
                  )}
                </Card>
              )}
              {workflowData && workflowCount < 10 && (
                <Card sx={{ p: 2, display: 'flex', gap: 5, flexDirection: 'column', flex: 1 }}>
                  <Typography variant="h6" sx={{ m: 0, p: 0 }}>
                    Workflow rules assigned to {name}
                  </Typography>
                  {/* TODO: determine what the data here looks like */}
                  {workflowData && (
                    <List sx={{ m: 0, p: 0 }}>
                      {workflowData?.map((workflow: any) => {
                        return (
                          <ListItem key={workflow.SmartRuleId} sx={{ m: 0, p: 0 }}>
                            <Link href={`/workflow-rules/${workflow.SmartRuleId}?variant=edit&step=0&completed=%7B%7D`}>
                              Workflow ID: {workflow.SmartRuleId} - {workflow.Description}
                            </Link>
                          </ListItem>
                        );
                      })}
                    </List>
                  )}
                </Card>
              )}
            </Stack>
          </DialogContent>
        </>
      )}
      <Divider />
      <DialogActions sx={{ m: 2 }}>
        <IconButton
          id="closeModalIcon"
          color="default"
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
          }}
          onClick={onClose}>
          <CloseIcon />
        </IconButton>
        <Button id="cancelBtn" color="error" onClick={onClose} size="small" variant="outlined">
          OK
        </Button>
      </DialogActions>
    </MuiDialog>
  );
};

export default FailedToArchiveModal;
